import {
  UiSelect,
  UiTextarea,
  UiInput,
  UiDatepicker,
  UiButton,
  UiAdvancedSelect,
} from "@/components/ui";
import { useCampagne } from "@/composables/campagne";
import { useCommande, AddCommandeDTOQuerySchema } from "@/composables/commande";
import { ChangeEvent, useEffect, useState } from "react";
import { format, parse, set } from "date-fns";
import { useCities } from "@/composables/cities";
import { UserTypes, UserTypesCodes, useUsers } from "@/composables/users";
import { ZodError } from "zod";
import { useValidationErrors } from "@/utils";
import InfoLotsForm from "./info.lots.form";
import { CommandModel, UserModel } from "@/app/entities";
import { useToast } from "@/composables/toast";
import isBefore from "date-fns/isBefore";

interface CommandeFormProps {
  initialValue?: Partial<CommandModel>;
}
export function CommandeForm({ initialValue }: CommandeFormProps) {
  const [campagneReference, setCampagneReference] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const {
    loading: loadingCampagnes,
    campagnes,
    getCampagneByReference,
    fetchCampagnes,
  } = useCampagne();
  const { loading, saveCommande } = useCommande();
  const {
    cities,
    loading: loadingVilles,
    fetchCities,
    fetchRegionCities,
  } = useCities();
  const { loading: loadingUsers, getUsers } = useUsers();
  const { setError, getFieldErrors } = useValidationErrors();
  const [regionCities, setRegionCities] = useState<string[]>([]);
  const [formValidation, setFormValidation] = useState(false);
  const [users, setUsers] = useState<UserModel[]>([]);
  const [infosLot, setInfosLot] = useState([
    {
      ville: "",
      nblots: 1,
    },
  ]);

  useEffect(() => {
    if (initialValue) {
      setCampagneReference(initialValue?.campagne?.reference ?? "");

      const defaultDate = initialValue?.dfs
        ? parse(initialValue?.dfs ?? "", "yyyy-MM-dd", new Date())
        : null;
      setStartDate(defaultDate ? format(defaultDate, "yyyy-MM-dd") : "");
    }
  }, [initialValue]);

  useEffect(() => {
    fetchCampagnes();
  }, []);

  useEffect(() => {
    const campagne = getCampagneByReference(campagneReference);
    if (!campagne) return;
    getUsers({ type: UserTypes.FOURNISSEUR, size: 1000 }).then((users) => {
      setUsers(users);
    });
    fetchCities();
    fetchRegionCities(campagne.region).then((data) => {
      setRegionCities(data);
    });
  }, [campagneReference]);

  useEffect(() => {
    checkFormValidity();
  }, [campagneReference, startDate, infosLot]);

  const checkFormValidity = () => {
    const formElement = document.querySelector(
      ".campagn-form"
    ) as HTMLFormElement;
    if (!formElement) return;
    const TheFormData = new FormData(formElement);

    try {
      const fcode = TheFormData.get("fournisseur") as string;
      const fournisseur = users.find(
        (user: UserModel) => user.matricule === fcode
      ) as UserModel;
      const params = {
        referenceCampagne: campagneReference,
        villeLivraison: TheFormData.get("villeLivraison") as string,
        dfs: startDate,
        detailAdresse: TheFormData.get("detailAdresse") as string,
        commentaire: TheFormData.get("commentaire") as string,
        infosLot: infosLot,
      };

      AddCommandeDTOQuerySchema.parse(params);
      setFormValidation(true);
    } catch (error) {
      console.log(error);
      setFormValidation(false);
    }
  };

  const handleSubmit = async () => {
    const formElement = document.querySelector(
      ".campagn-form"
    ) as HTMLFormElement;
    if (!formElement) return;
    const TheFormData = new FormData(formElement);
    try {
      const fcode = TheFormData.get("fournisseur") as string;
      const fournisseur = users.find(
        (user: UserModel) => user.matricule === fcode
      ) as UserModel;
      const params = {
        referenceCampagne: campagneReference,
        villeLivraison: TheFormData.get("villeLivraison") as string,
        dfs: startDate,
        detailAdresse: TheFormData.get("detailAdresse") as string,
        commentaire: TheFormData.get("commentaire") as string,
        infosLot: infosLot,
      };

      const validatedData = AddCommandeDTOQuerySchema.parse(params);
      saveCommande({ ...validatedData, fournisseur });
    } catch (error) {
      if (error instanceof ZodError) {
        setError(error);
        useToast("Erreur de validation", { variant: "error" });
        return;
      }
      useToast("Une erreur s'est produite", { variant: "error" });
    }
  };

  return (
    <div>
      <h2 className="form-title">Créez votre commande en quelques clics</h2>
      <div className="form-subtitle py-2">
        Organisez facilement un recensement ou une campagne de vaccination pour
        votre cheptel. Planifiez, informez et suivez vos actions en toute
        simplicité.
      </div>
      <form className="campagn-form mt-4">
        <div className="grid gap-4 gap-y-2 grid-cols-1 md:grid-cols-2">
          <div className="w-full ">
            <UiAdvancedSelect
              className="w-full"
              label="Campagne"
              name="referenceCampagne"
              loading={loadingCampagnes}
              defaultValue={campagneReference}
              onChange={(v: string | null) => setCampagneReference(v ?? "")}
              placeholder="Choisissez une option"
              options={campagnes
                .filter((campagne) => !campagne.draft && !campagne.shelve)
                .map((campagne) => ({
                  value: campagne.reference,
                  label: campagne.nom,
                }))}
              errors={getFieldErrors("referenceCampagne")}
            ></UiAdvancedSelect>
          </div>
        </div>

        {campagneReference && (
          <div>
            <div className="grid gap-4 gap-y-2 grid-cols-1 md:grid-cols-2">
              <div className="w-full ">
                <UiAdvancedSelect
                  className="w-full"
                  label="Fournisseur"
                  name="fournisseur"
                  loading={loadingUsers}
                  defaultValue={initialValue?.fournisseur?.matricule ?? ""}
                  placeholder="Choisissez une option"
                  options={users.map((user: UserModel) => ({
                    value: user.matricule,
                    label: `${user.nom} ${user.prenom} (${user.matricule})`,
                  }))}
                  errors={getFieldErrors("fournisseur")}
                ></UiAdvancedSelect>
              </div>
              <div className="w-full ">
                <UiAdvancedSelect
                  className="w-full"
                  label="Ville de livraison"
                  name="villeLivraison"
                  loading={loadingVilles}
                  defaultValue={initialValue?.villeLivraison ?? ""}
                  placeholder="Choisissez une option"
                  options={cities.map((ville) => ({
                    value: ville,
                    label: ville,
                  }))}
                  errors={getFieldErrors("villeLivraison")}
                ></UiAdvancedSelect>
              </div>
              <div className="w-full ">
                <UiDatepicker
                  name="dfs"
                  className="w-full"
                  label="Date de fabrication souhaitée"
                  shouldDisableDate={(date) => isBefore(date, new Date())}
                  defaultValue={
                    initialValue?.dfs
                      ? parse(initialValue?.dfs, "yyyy-MM-dd", new Date())
                      : null
                  }
                  onChange={(date: Date | null) => {
                    setStartDate(date ? format(date, "yyyy-MM-dd") : "");
                  }}
                  errors={getFieldErrors("dfs")}
                />
              </div>

              <div className="w-full ">
                <UiInput
                  label="Détail de l'adresse"
                  placeholder="Détail de l'adresse"
                  className="w-full"
                  name="detailAdresse"
                  defaultValue={initialValue?.detailAdresse ?? ""}
                  onChange={(v: ChangeEvent<HTMLInputElement>) => {
                    checkFormValidity();
                  }}
                  errors={getFieldErrors("detailAdresse")}
                ></UiInput>
              </div>
            </div>

            <div className="mt-4">
              <div className="w-full ">
                <UiTextarea
                  label="Commentaire"
                  placeholder="Commentaire"
                  className="w-full"
                  name="commentaire"
                  defaultValue={initialValue?.commentaire ?? ""}
                  onChange={(v: ChangeEvent<HTMLTextAreaElement>) => {
                    checkFormValidity();
                  }}
                  errors={getFieldErrors("commentaire")}
                ></UiTextarea>
              </div>
            </div>

            <div className="mt-4">
              <InfoLotsForm
                cities={regionCities}
                onChange={(
                  v: {
                    ville: string;
                    nblots: number;
                  }[]
                ) => setInfosLot(v)}
              />
            </div>

            <div className="mt-8">
              <UiButton
                onClick={handleSubmit}
                disabled={!formValidation}
                loading={loading}
              >
                Enregistrer la commande
              </UiButton>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
